var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { getReadableError } from 'root/src/client/v2/common/helpers/getReadableError';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Select } from 'root/src/client/v2/common/components/ui/select';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
// Redesign "Add to Program" modal for the creator profile header. The shared
// `RedesignAddCreatorToProgramModal` (campaign-creators redesign) sends a `{ platform, platformId }`
// identity (the dashboard-table path); the creator profile has the resolved `userId` directly, so this
// sibling sends the `{ userId }` identity instead. Same redesign shell + Select + useAsyncAction + sonner,
// wired to the SAME tRPC procedures: `ambassador.getBrandPrograms` (picker) and
// `ambassador.addCreatorsToProgram` (mutation). Both are gated `BrandEnterprise` server-side.
export const RedesignAddCreatorToProgramModal = ({ userId, displayName }) => {
    var _a;
    const { hide } = useRedesignModal();
    const utils = trpc.useUtils();
    const programsQuery = trpc.ambassador.getBrandPrograms.useQuery(undefined, { staleTime: 60000 });
    const addCreators = trpc.ambassador.addCreatorsToProgram.useMutation();
    const { loading, success, error, run } = useAsyncAction();
    const [programId, setProgramId] = useState('');
    const programs = (_a = programsQuery.data) !== null && _a !== void 0 ? _a : [];
    const options = programs.map(program => ({ value: program.id, label: program.title }));
    const submit = () => {
        if (!programId) {
            toast.error('Select a program first.');
            return;
        }
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            const { addedCreatorsCount, duplicatedCreators } = yield addCreators.mutateAsync({
                programId,
                creators: [{ userId }],
            });
            void utils.ambassador.getCreatorsList.invalidate();
            if (duplicatedCreators.length) {
                throw new Error(`${displayName} is already in this program.`);
            }
            if (!addedCreatorsCount) {
                throw new Error('No creators were added.');
            }
        }), {
            onSuccess: hide,
            toast: {
                loading: 'Adding to program...',
                success: `${displayName} added to program.`,
                error: caught => getReadableError(caught, 'Could not add the creator to the program.'),
            },
        });
    };
    return (<RedesignModalShell className="p-0 sm:max-w-md">
			<div className="flex w-full flex-col">
				<div className="flex items-center gap-3 border-b border-solid border-white/[0.06] px-5 py-4">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
						<GroupAddOutlinedIcon sx={{ fontSize: 18 }}/>
					</div>
					<div className="min-w-0">
						<h2 className="font-display text-base font-semibold text-foreground">Add to program</h2>
						<p className="truncate text-xs text-muted-foreground">{displayName}</p>
					</div>
				</div>

				<div className="flex flex-col gap-2 px-5 py-4">
					<label htmlFor="program" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Program</label>
					{programsQuery.isLoading ? (<Skeleton className="h-9 w-full rounded-lg"/>) : options.length ? (<Select id="program" value={programId} onValueChange={setProgramId} options={options} placeholder="Select a program" searchable searchPlaceholder="Search programs..."/>) : (<p className="rounded-lg border border-dashed border-white/[0.1] px-3 py-4 text-center text-xs text-muted-foreground">
							You have no ambassador programs yet.
						</p>)}
				</div>

				<div className="flex justify-end gap-2 border-t border-solid border-white/[0.06] px-5 py-4">
					<Button type="button" variant="secondary" onClick={hide} className="rounded-lg text-sm">Cancel</Button>
					<Button type="button" variant="brand" loading={loading} success={success} error={error} disabled={!options.length} onClick={submit} className="rounded-lg text-sm font-semibold">
						Add to program
					</Button>
				</div>
			</div>
		</RedesignModalShell>);
};
